import { useCallback, useEffect, useState } from "react";

export type Theme = "dark" | "light";
export type ThemeMode = "light" | "dark" | "system";

const THEME_KEY = "bv2:theme";
const MODE_KEY = "bv2:theme-mode";

/** The user's appearance preference. "system" follows the OS via
 *  prefers-color-scheme; "light" / "dark" pin a specific resolution.
 *  `bv2:theme` (THEME_KEY) holds the *resolved* dark/light value so
 *  the inline bootstrap in index.html can paint correctly on first
 *  byte without needing to re-query matchMedia. */
function readSavedMode(): ThemeMode | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const v = localStorage.getItem(MODE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
    // Pre-system-mode users only have THEME_KEY set. Treat that saved
    // light/dark as an explicit (non-system) preference so we don't
    // silently relabel them as following the OS.
    const legacy = localStorage.getItem(THEME_KEY);
    if (legacy === "light" || legacy === "dark") return legacy;
    return null;
  } catch {
    return null;
  }
}

function readOSPref(): Theme {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveMode(m: ThemeMode): Theme {
  return m === "system" ? readOSPref() : m;
}

let mode: ThemeMode = readSavedMode() ?? "dark";
let current: Theme = resolveMode(mode);
const themeListeners = new Set<(t: Theme) => void>();
const modeListeners = new Set<(m: ThemeMode) => void>();

function applyToDocument(theme: Theme) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  if (theme === "dark") {
    html.classList.add("dark");
    html.classList.remove("light");
  } else {
    html.classList.add("light");
    html.classList.remove("dark");
  }
}

applyToDocument(current);

// matchMedia subscription only fires while mode === "system". The
// listener stays mounted once attached - cheap to leave running, and
// switching back to system later doesn't need to re-bind.
let mediaQuery: MediaQueryList | null = null;
function ensureMediaListener() {
  if (typeof window === "undefined" || !window.matchMedia) return;
  if (mediaQuery) return;
  mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = (e: MediaQueryListEvent) => {
    if (mode !== "system") return;
    const next: Theme = e.matches ? "dark" : "light";
    if (next === current) return;
    current = next;
    persistResolved(next);
    applyToDocument(next);
    for (const l of themeListeners) l(next);
  };
  if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", handler);
  // Older Safari fallback.
  else if ((mediaQuery as MediaQueryList & { addListener?: (fn: (e: MediaQueryListEvent) => void) => void }).addListener) {
    (mediaQuery as MediaQueryList & { addListener: (fn: (e: MediaQueryListEvent) => void) => void }).addListener(handler);
  }
}

if (mode === "system") ensureMediaListener();

function persistMode(m: ThemeMode) {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(MODE_KEY, m);
  } catch {
    /* private mode etc. */
  }
}

function persistResolved(t: Theme) {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(THEME_KEY, t);
  } catch {
    /* private mode etc. */
  }
}

export function setMode(next: ThemeMode) {
  mode = next;
  persistMode(next);
  if (next === "system") ensureMediaListener();
  const resolved = resolveMode(next);
  current = resolved;
  persistResolved(resolved);
  applyToDocument(resolved);
  for (const l of themeListeners) l(resolved);
  for (const l of modeListeners) l(next);
}

export function setTheme(theme: Theme) {
  // Picking a concrete light/dark value also pins the mode, so the
  // header sun/moon toggle reliably exits system mode in one click.
  setMode(theme);
}

export function toggleTheme() {
  setTheme(current === "dark" ? "light" : "dark");
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const [theme, setLocal] = useState<Theme>(current);
  useEffect(() => {
    const fn = (t: Theme) => setLocal(t);
    themeListeners.add(fn);
    return () => {
      themeListeners.delete(fn);
    };
  }, []);
  const toggle = useCallback(() => toggleTheme(), []);
  return { theme, setTheme, toggle };
}

export function useThemeMode(): { mode: ThemeMode; setMode: (m: ThemeMode) => void } {
  const [m, setLocal] = useState<ThemeMode>(mode);
  useEffect(() => {
    const fn = (next: ThemeMode) => setLocal(next);
    modeListeners.add(fn);
    return () => {
      modeListeners.delete(fn);
    };
  }, []);
  return { mode: m, setMode };
}
